import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import RentalSurveyHistoryCard from "./RentalSurveyHistoryCard";
import { entryMatchesNoteFilter, isNoteFilterActive, normalizeEntry } from "./utils";

const getDateRank = (surveyDate) => {
  if (!surveyDate) return Number.POSITIVE_INFINITY;
  const parsed = new Date(surveyDate).getTime();
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

const sortEntriesLatestFirst = (entries = []) =>
  (Array.isArray(entries) ? entries : [])
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const aRank = getDateRank(a.entry?.surveyDate);
      const bRank = getDateRank(b.entry?.surveyDate);
      if (aRank !== bRank) return bRank - aRank;
      return a.index - b.index;
    })
    .map(({ entry }) => entry);

/**
 * @typedef {import("./types").RentalHistoryEntry} RentalHistoryEntry
 * @typedef {import("./types").RentalHistoryTabProps} RentalHistoryTabProps
 */

/**
 * @param {RentalHistoryTabProps} props
 */
const RentalHistoryTab = ({ propertyName, entries = [], onChange, tagOptions = [], noteFilter = null }) => {
  const [localEntries, setLocalEntries] = useState(() =>
    (Array.isArray(entries) ? entries : []).map(normalizeEntry)
  );

  useEffect(() => {
    setLocalEntries((Array.isArray(entries) ? entries : []).map(normalizeEntry));
  }, [entries]);

  const emitChange = (nextEntries) => {
    setLocalEntries(nextEntries);
    if (typeof onChange === "function") {
      onChange(nextEntries);
    }
  };

  const handleUpdateCard = (cardId, nextEntry) => {
    const nextEntries = localEntries.map((entry) =>
      entry.id === cardId ? normalizeEntry(nextEntry) : entry
    );
    emitChange(nextEntries);
  };

  const handleDeleteCard = (cardId) => {
    const nextEntries = localEntries.filter((entry) => entry.id !== cardId);
    emitChange(nextEntries);
  };

  const renderedEntries = useMemo(
    () =>
      sortEntriesLatestFirst((Array.isArray(localEntries) ? localEntries : []).map(normalizeEntry)).filter(
        (entry) => entryMatchesNoteFilter(entry, noteFilter)
      ),
    [localEntries, noteFilter]
  );
  const filterActive = isNoteFilterActive(noteFilter);

  return (
    <Box sx={{ position: "relative", pb: 2 }}>
      <Stack spacing={2}>
        {renderedEntries.length === 0 && filterActive ? (
          <Box
            sx={{
              borderRadius: 2,
              border: "1px dashed #CBD5E1",
              backgroundColor: "#F8FAFC",
              px: 2,
              py: 3,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#334155" }}>
              No notes match the current search.
            </Typography>
          </Box>
        ) : renderedEntries.length === 0 ? (
          <Box
            sx={{
              borderRadius: 2,
              border: "1px dashed #CBD5E1",
              backgroundColor: "#F8FAFC",
              px: 2,
              py: 3,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#334155" }}>
              No rental survey history yet for {propertyName || "this property"}.
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: "12px", color: "#64748B" }}>
              Click + Survey next to Save to add a snapshot.
            </Typography>
          </Box>
        ) : (
          renderedEntries.map((entry) => (
            <RentalSurveyHistoryCard
              key={entry.id}
              entry={entry}
              onUpdate={(nextEntry) => handleUpdateCard(entry.id, nextEntry)}
              onDelete={() => handleDeleteCard(entry.id)}
              tagOptions={tagOptions}
              noteFilter={noteFilter}
            />
          ))
        )}
      </Stack>
    </Box>
  );
};

export default RentalHistoryTab;
